/**
 * Katman 0 — uyaran şekillerinin tek kaynağı.
 *
 * NEDEN VAR
 *
 * Uyaranlar Unicode karakterdi: `'●' '■' '▲' '★' '◆' '♥'`, karıştırıcı
 * küme `'◐' '◑' '◒' '◓' '◔' '◕'`. Metin olarak çiziliyorlardı — hem
 * ekranda hem A4'te. Bu, uyaranın görünüşünü YAZI TİPİNE bırakıyordu:
 * tablette bir font, veli telefonunda başka font, yazıcıda üçüncüsü.
 * `◐` ile `◑` ayrımı zaten kıl payı; font değişince kıl payı ya
 * kayboluyor ya büyüyor.
 *
 * Bunun bedeli doğrudan ölçümde: "aynı tohum aynı sayfa" sözü cihazlar
 * arasında tutmuyordu. Aynı veri, farklı görünen uyaran demek, aynı
 * düzeyin çocuktan çocuğa farklı zorlukta olması demek — karıştırıcı
 * kümede doğrudan bir zorluk kayması.
 *
 * Emoji bu sorunu büyütürdü, çözmezdi: renkli, platforma özgü,
 * genişliği oynak. Uyaran alanında emoji yok.
 *
 * ÇÖZÜM
 *
 * Kapalı bir şekil kümesi, geometrisi burada tanımlı. Yazdırma
 * (`print.ts`) ve çocuk uygulaması AYNI bu dosyayı okuyor; iki kopya
 * tutmanın bedelini `EGZERSIZ_ADI` ile bir kez ödedik.
 *
 * Kazanç: her cihazda aynı piksel, yazıcıda aynı kontur, dolgu ve
 * çizgi kalınlığı bizim elimizde, karıştırıcı kümeyi artık biz
 * ayarlıyoruz.
 *
 * `glyph` KALKMIYOR: harf, rakam ve kelime hâlâ metin. Kalkan şey,
 * şekil anlatmak için metin kullanmak.
 */

export type ShapeName =
  /* Sade küme — altısı birbirinden açıkça ayrı */
  | 'daire'
  | 'kare'
  | 'ucgen'
  | 'yildiz'
  | 'elmas'
  | 'kalp'
  /* Aynı altısının içi boş hali: şekil aynı, dolgu farklı */
  | 'daire_bos'
  | 'kare_bos'
  | 'ucgen_bos'
  | 'yildiz_bos'
  | 'elmas_bos'
  | 'kalp_bos'
  /**
   * Karıştırıcı küme — hepsi aynı daire, farkı yalnızca dolu bölge.
   *
   * Eski `◐ ◑ ◒ ◓ ◔ ◕` kümesinin yaptığı işi yapıyor ama sınırı artık
   * fontun değil bizim elimizde: çember her zaman çiziliyor, dolu
   * bölgenin kenarı tam ortadan geçiyor.
   */
  | 'yarim_sol'
  | 'yarim_sag'
  | 'yarim_ust'
  | 'yarim_alt'
  | 'ceyrek'
  | 'ucceyrek'
  /* Yön kümesi */
  | 'ok_yukari'
  | 'ok_sag_yukari'
  | 'ok_sag'
  | 'ok_sag_asagi'
  | 'ok_asagi'
  | 'ok_sol_asagi'
  | 'ok_sol'
  | 'ok_sol_yukari'

export interface SekilTanimi {
  /** SVG yol verisi, 0–100 kutusunda */
  d: string
  /** Dolu mu, yoksa yalnızca kontur mu */
  dolu: boolean
  /**
   * Yolun üstüne ayrıca tam çember çizilsin mi.
   *
   * Karıştırıcı küme için şart: `yarim_sol` yalnız dolu yarımdan
   * ibaret olsaydı yarım ay gibi okunurdu. Çember, "bu bir dairedir,
   * yarısı dolu" bilgisini taşıyor.
   */
  cember?: boolean
  /** Derece cinsinden dönme (yalnız oklarda; geometri bizim elimizde) */
  donme?: number
}

/* ------------------------------------------------------------------ *
 * Yollar
 *
 * Hepsi 0–100 kutusunda ve birbirine yakın görsel ağırlıkta çizildi.
 * Dolu ve boş hali AYNI yolu paylaşıyor: ikisinin kenarı birebir
 * çakışsın diye. `●` ile `○` arasındaki fark fontta boyut farkı da
 * içeriyordu; burada içermiyor.
 * ------------------------------------------------------------------ */

const DAIRE = 'M10 50A40 40 0 1 0 90 50A40 40 0 1 0 10 50Z'
const KARE = 'M14 14H86V86H14Z'
const UCGEN = 'M50 10L90 84H10Z'
const YILDIZ =
  'M50 5L60.6 35.4L92.8 36.1L67.1 55.6L76.5 86.4L50 68L23.5 86.4L32.9 55.6L7.2 36.1L39.4 35.4Z'
const ELMAS = 'M50 8L92 50L50 92L8 50Z'
const KALP = 'M50 88C50 88 12 62 12 38A21 21 0 0 1 50 25A21 21 0 0 1 88 38C88 62 50 88 50 88Z'

/**
 * Ok: gövde artı uç, yukarı bakıyor.
 *
 * Diğer yedi yön bunun 45°'nin katlarıyla döndürülmüş hali. Burada
 * döndürmek güvenli — `zihinde çevirme`de olduğu gibi bir fontun taban
 * çizgisini değil, kendi tanımladığımız yolu döndürüyoruz.
 */
const OK = 'M50 10L80 46H64V88H36V46H20Z'

export const SEKILLER: Record<ShapeName, SekilTanimi> = {
  daire: { d: DAIRE, dolu: true },
  kare: { d: KARE, dolu: true },
  ucgen: { d: UCGEN, dolu: true },
  yildiz: { d: YILDIZ, dolu: true },
  elmas: { d: ELMAS, dolu: true },
  kalp: { d: KALP, dolu: true },

  daire_bos: { d: DAIRE, dolu: false },
  kare_bos: { d: KARE, dolu: false },
  ucgen_bos: { d: UCGEN, dolu: false },
  yildiz_bos: { d: YILDIZ, dolu: false },
  elmas_bos: { d: ELMAS, dolu: false },
  kalp_bos: { d: KALP, dolu: false },

  yarim_sol: { d: 'M50 10A40 40 0 0 0 50 90Z', dolu: true, cember: true },
  yarim_sag: { d: 'M50 10A40 40 0 0 1 50 90Z', dolu: true, cember: true },
  yarim_ust: { d: 'M10 50A40 40 0 0 1 90 50Z', dolu: true, cember: true },
  yarim_alt: { d: 'M10 50A40 40 0 0 0 90 50Z', dolu: true, cember: true },
  ceyrek: { d: 'M50 50L50 10A40 40 0 0 1 90 50Z', dolu: true, cember: true },
  ucceyrek: { d: 'M50 50L50 10A40 40 0 1 1 10 50Z', dolu: true, cember: true },

  ok_yukari: { d: OK, dolu: true },
  ok_sag_yukari: { d: OK, dolu: true, donme: 45 },
  ok_sag: { d: OK, dolu: true, donme: 90 },
  ok_sag_asagi: { d: OK, dolu: true, donme: 135 },
  ok_asagi: { d: OK, dolu: true, donme: 180 },
  ok_sol_asagi: { d: OK, dolu: true, donme: 225 },
  ok_sol: { d: OK, dolu: true, donme: 270 },
  ok_sol_yukari: { d: OK, dolu: true, donme: 315 },
}

/**
 * Uygulayıcıya ve yönergeye giden Türkçe adlar.
 *
 * Yönerge sesli okunuyor ("daire şeklinin hepsini işaretle") ve kâğıt
 * cevap anahtarı okunabilir olmak zorunda. Anahtarda 3 mm'lik bir
 * `◆` çizmek, uygulayıcıyı sayfaya eğilip şekil ayırt etmeye zorluyordu.
 */
export const SEKIL_ADI: Record<ShapeName, string> = {
  daire: 'daire',
  kare: 'kare',
  ucgen: 'üçgen',
  yildiz: 'yıldız',
  elmas: 'elmas',
  kalp: 'kalp',
  daire_bos: 'içi boş daire',
  kare_bos: 'içi boş kare',
  ucgen_bos: 'içi boş üçgen',
  yildiz_bos: 'içi boş yıldız',
  elmas_bos: 'içi boş elmas',
  kalp_bos: 'içi boş kalp',
  yarim_sol: 'sol yarısı dolu daire',
  yarim_sag: 'sağ yarısı dolu daire',
  yarim_ust: 'üst yarısı dolu daire',
  yarim_alt: 'alt yarısı dolu daire',
  ceyrek: 'çeyreği dolu daire',
  ucceyrek: 'dörtte üçü dolu daire',
  ok_yukari: 'yukarı ok',
  ok_sag_yukari: 'sağ üst ok',
  ok_sag: 'sağa ok',
  ok_sag_asagi: 'sağ alt ok',
  ok_asagi: 'aşağı ok',
  ok_sol_asagi: 'sol alt ok',
  ok_sol: 'sola ok',
  ok_sol_yukari: 'sol üst ok',
}

export const SEKIL_ADLARI = Object.keys(SEKILLER) as ShapeName[]

const KUME = new Set<string>(SEKIL_ADLARI)

/** Bu metin bir şekil adı mı? Çizim tarafı bununla metinden ayırıyor. */
export function sekilMi(ad: string | undefined): ad is ShapeName {
  return ad !== undefined && KUME.has(ad)
}

/**
 * Bir uyaran: ya çizilen şekil, ya metin (harf, rakam, kelime).
 *
 * İkisi AYNI alanda taşınmıyor. Tek bir `string` alanı kullanmak
 * mümkündü — şekil adları ('daire') harf ve rakamlarla çakışmıyor —
 * ama o zaman "bu hücre ne gösteriyor" sorusunun cevabı türde değil
 * çalışma anında bir arama tablosunda olurdu. Türün söylemediği şeyi
 * kimse okumaz.
 */
export interface Isaret {
  shape?: ShapeName
  glyph?: string
}

/**
 * Uyaranın KİMLİĞİ: cevapta, çözümde ve karşılaştırmada kullanılan dize.
 *
 * Şekil için şekil adı, metin için metnin kendisi. Puanlama bu dizeyi
 * karşılaştırıyor; çocuğun cihazı da seçimini bu dizeyle bildiriyor.
 */
export function isaretKimligi(i: Isaret): string {
  return i.shape ?? i.glyph ?? ''
}

/**
 * Uyaranın uygulayıcıya okunacak hali.
 *
 * Yönergede ve cevap anahtarında geçiyor; çocuğun ekranında değil.
 */
export function isaretMetni(i: Isaret): string {
  return i.shape ? SEKIL_ADI[i.shape] : (i.glyph ?? '')
}

/**
 * Şeklin SVG'si.
 *
 * Tek bir `<svg>` dizesi dönüyor; hem `print.ts` hem çocuk uygulaması
 * bunu olduğu gibi gömüyor. `currentColor` kullanılıyor ki rengi saran
 * öğe belirlesin — koyu tema, gri tonlamalı yazıcı ve vurgulu hücre
 * aynı şekli farklı renkte çizebilsin.
 *
 * `stroke-width` dolu şekilde de verilmiş durumda: çok küçük basıldığında
 * (14 sütunlu tarama ızgarası) dolgu tek başına inceliyor ve `elmas` ile
 * `daire` birbirine yaklaşıyordu.
 */
export function sekilSvg(ad: ShapeName, boyutPx?: number): string {
  const s = SEKILLER[ad]
  const olcu = boyutPx ? ` width="${boyutPx}" height="${boyutPx}"` : ''
  const donus = s.donme ? ` transform="rotate(${s.donme} 50 50)"` : ''
  const cember = s.cember
    ? '<path d="' + DAIRE + '" fill="none" stroke="currentColor" stroke-width="7"/>'
    : ''
  const dolgu = s.dolu
    ? 'fill="currentColor" stroke="currentColor" stroke-width="2"'
    : 'fill="none" stroke="currentColor" stroke-width="9"'
  return (
    `<svg viewBox="0 0 100 100"${olcu} xmlns="http://www.w3.org/2000/svg" ` +
    `role="img" aria-label="${SEKIL_ADI[ad]}" focusable="false">` +
    `<path d="${s.d}" ${dolgu}${donus}/>${cember}</svg>`
  )
}
